import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/SossietyDialog';
import { DialogFooter } from '@/components/ui/dialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/shared/hooks/use-toast';
import { DateInput } from '@/components/ui/DateInput';
import { useCreatePromotion, useUpdatePromotion, useUserPromotions } from '@/features/profile/hooks/usePromotions';
import { UserPromotion } from '@/types/promotions';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';
import { BELT_ORDER, getBeltIndex } from '@/constants/belt';

const validateBeltProgression = (currentBelt: string, existingPromotions: UserPromotion[], t: any): { isValid: boolean; message?: string } => {
  // Removed validation that forces white belt first - users can add any promotion
  // Removed validation that prevents skipping belts - users can add promotions in any order
  
  return { isValid: true };
};

const validateDuplicatePromotion = (
  beltColor: string, 
  stripeCount: number, 
  existingPromotions: UserPromotion[], 
  editingPromotion: UserPromotion | null | undefined,
  t: any
): { isValid: boolean; message?: string } => {
  const duplicate = existingPromotions.find(promotion => 
    promotion.belt_color === beltColor && 
    promotion.stripe_count === stripeCount &&
    promotion.id !== editingPromotion?.id // Allow editing the same promotion
  );

  if (duplicate) {
    const stripeText = stripeCount === 0 ? '' : 
                     stripeCount === 1 ? t('profile.promotion.with_1st_stripe') :
                     stripeCount === 2 ? t('profile.promotion.with_2nd_stripe') :
                     stripeCount === 3 ? t('profile.promotion.with_3rd_stripe') : t('profile.promotion.with_4th_stripe');
    
    return { 
      isValid: false, 
      message: t('profile.promotion.belt_already_registered', { 
        beltColor: beltColor.charAt(0).toUpperCase() + beltColor.slice(1),
        stripeText 
      }) 
    };
  }

  return { isValid: true };
};

interface AddPromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  promotionToEdit?: UserPromotion | null;
}

export const AddPromotionModal: React.FC<AddPromotionModalProps> = ({
  isOpen,
  onClose,
  promotionToEdit
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const createPromotionMutation = useCreatePromotion();
  const updatePromotionMutation = useUpdatePromotion();
  const { t } = useI18n();
  
  // Get existing promotions for validation
  const { data: existingPromotions = [] } = useUserPromotions(user?.id || '');

  // Form state
  const [beltColor, setBeltColor] = useState<'white' | 'blue' | 'purple' | 'brown' | 'black' | undefined>(undefined);
  const [stripeCount, setStripeCount] = useState<number>(0);
  const [graduationDate, setGraduationDate] = useState<string>('');
  const [instructorValue, setInstructorValue] = useState<any>(null);
  const [academyValue, setAcademyValue] = useState<{ id: string; name: string } | ''>('');

  // All fields should always be shown, including date for white belt
  const shouldShowAdditionalFields = true;

  // Simple date mask function
  const applyDateMask = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4)}`;
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  };

  // Simple date validation
  const isValidDate = (dateString: string) => {
    if (!dateString || dateString.length !== 10) return false;
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;
  };
  const [validationError, setValidationError] = useState<string>('');
  
  // User profile data for auto-filling
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Consider it editing only if it's a real promotion (not virtual)
  const isEditing = !!promotionToEdit && promotionToEdit.id !== 'virtual-current-belt';
  const isLoading = createPromotionMutation.isPending || updatePromotionMutation.isPending;

  // Helper function to handle academy value changes
  const handleAcademyChange = (value: string | { id: string; name: string }) => {
    setAcademyValue(value as { id: string; name: string } | '');
  };

  // Fetch user profile data for auto-filling
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id || !isOpen) return; // Only fetch when modal is open
      
      setIsLoadingProfile(true);
      try {

        
        // Get user profile with academy and instructors
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select(`
            *,
            main_academy:main_academy_id(id, name)
          `)
          .eq('id', user.id);



        if (profileData && profileData.length > 0) {
          const profile = profileData[0];
          // Get user's instructors (simplified approach)
          const { data: instructorsData, error: instructorsError } = await supabase
            .from('profile_instructors')
            .select('instructor_user_id')
            .eq('profile_id', user.id)
            .limit(1);



          let primaryInstructor = null;
          
          if (instructorsData && instructorsData[0]?.instructor_user_id) {
            // Get the instructor profile separately
            const { data: instructorProfiles, error: instructorError } = await supabase
              .from('profiles')
              .select('id, full_name, username')
              .eq('id', instructorsData[0].instructor_user_id);
            
            // Only set instructor if the profile exists and has a valid username
            if (instructorProfiles && instructorProfiles.length > 0 && instructorProfiles[0].username) {
              primaryInstructor = instructorProfiles[0];
            } else {
              console.warn('Instructor profile not accessible or missing username:', instructorsData[0].instructor_user_id);
            }
          }

          const userProfileData = {
            ...profile,
            primaryInstructor
          };


          setUserProfile(userProfileData);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [user?.id, isOpen]); // Added isOpen to dependencies

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (promotionToEdit) {
        // If it's a virtual promotion, treat it as a new promotion to create
        const isVirtual = promotionToEdit.id === 'virtual-current-belt';
        
        if (isVirtual) {
          // For virtual promotions, we'll create a new one instead of editing
          // Pre-fill with the virtual promotion data but treat as new
          setBeltColor(promotionToEdit.belt_color);
          setStripeCount(promotionToEdit.stripe_count);
          setGraduationDate('');
          setInstructorValue(null);
          setAcademyValue('');
        } else {
          // Pre-fill form for editing (real promotion) or converting (virtual promotion)
          setBeltColor(promotionToEdit.belt_color);
          setStripeCount(promotionToEdit.stripe_count);
        // Convert ISO date (yyyy-mm-dd or ISO string) to dd/mm/yyyy format
        // graduation_date is a DATE type (no time), so we parse it directly to avoid timezone issues
        if (promotionToEdit.graduation_date && !isVirtual) {
          try {
            let year: number, month: number, day: number;
            
            if (typeof promotionToEdit.graduation_date === 'string') {
              // If it's already in yyyy-mm-dd format, parse it directly
              if (/^\d{4}-\d{2}-\d{2}$/.test(promotionToEdit.graduation_date)) {
                const parts = promotionToEdit.graduation_date.split('-');
                year = parseInt(parts[0], 10);
                month = parseInt(parts[1], 10);
                day = parseInt(parts[2], 10);
              } else if (promotionToEdit.graduation_date.includes('T')) {
                // ISO string with time (e.g., "2025-12-02T00:00:00.000Z")
                // Use UTC methods to avoid timezone conversion
                const date = new Date(promotionToEdit.graduation_date);
                year = date.getUTCFullYear();
                month = date.getUTCMonth() + 1;
                day = date.getUTCDate();
              } else {
                // Fallback: try to extract date parts from string
                const date = new Date(promotionToEdit.graduation_date);
                year = date.getUTCFullYear();
                month = date.getUTCMonth() + 1;
                day = date.getUTCDate();
              }
              
              // Validate parsed values
              if (!isNaN(year) && !isNaN(month) && !isNaN(day) && year > 0 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                setGraduationDate(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`);
              } else {
                setGraduationDate('');
              }
            } else {
              setGraduationDate('');
            }
          } catch (error) {
            console.error('Error parsing graduation date:', error);
            setGraduationDate('');
          }
        } else {
          setGraduationDate('');
        }
          setInstructorValue(promotionToEdit.instructor_name || '');
          handleAcademyChange(promotionToEdit.school_name || '');
        }
      } else {
        // Reset form for new promotion - NO pre-fill for white belt
        setBeltColor(undefined); // No pre-fill - user must select
        setStripeCount(0);
        setGraduationDate('');
        
        // Auto-fill instructor if user has one
        if (userProfile?.primaryInstructor) {
          setInstructorValue(userProfile.primaryInstructor);
        } else {
          setInstructorValue(null);
        }
        
        // Auto-fill academy if user has one
        if (userProfile?.main_academy) {
          setAcademyValue(userProfile.main_academy as { id: string; name: string });
        } else {
          setAcademyValue('');
        }
      }
      setValidationError('');
    } else {
      // Clear user profile when modal closes to prevent unnecessary queries
      setUserProfile(null);
    }
  }, [isOpen, promotionToEdit, userProfile]);

  // Real-time validation effect
  useEffect(() => {
    if (!beltColor || !isOpen) {
      setValidationError('');
      return;
    }

    // Validate graduation date - now required for all belts including white
    // Only validate if user has entered something
    if (graduationDate) {
      if (!isValidDate(graduationDate)) {
        setValidationError(t('profile.promotion.invalid_date_format'));
        return;
      }
      
      // Parse yyyy-MM-dd to Date object
      const [year, month, day] = graduationDate.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day); // month - 1 because Date constructor uses 0-based months
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate > today) {
        setValidationError(t('profile.promotion.future_date_error'));
        return;
      }
    }

    // Removed belt progression validation - users can add any promotion in any order

    // Validate duplicate promotion
    const duplicateValidation = validateDuplicatePromotion(beltColor, stripeCount, existingPromotions, promotionToEdit, t);
    if (!duplicateValidation.isValid) {
      setValidationError(duplicateValidation.message || '');
      return;
    }

    setValidationError('');
  }, [beltColor, stripeCount, graduationDate, existingPromotions, promotionToEdit, isOpen]);

  // Validate form
  const validateForm = (): boolean => {
    if (!beltColor || beltColor === undefined) {
      setValidationError(t('profile.promotion.belt_color_required'));
      return false;
    }

    // Date is now required for all belts, including white
    if (!graduationDate || !isValidDate(graduationDate)) {
      setValidationError(t('profile.promotion.invalid_date_format'));
      return false;
    }

    // Check if date is in the future
    if (graduationDate && isValidDate(graduationDate)) {
      const [year, month, day] = graduationDate.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date > today) {
        setValidationError(t('profile.promotion.future_date_error'));
        return false;
      }
    }

    // Check if there are any validation errors
    if (validationError) {
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Extract instructor name
      const instructorName = typeof instructorValue === 'string' 
        ? instructorValue 
        : (instructorValue && typeof instructorValue === 'object' 
          ? instructorValue.full_name || instructorValue.name || '' 
          : '');

      // Extract academy name
      const schoolName = typeof academyValue === 'string' 
        ? academyValue 
        : (academyValue && typeof academyValue === 'object' 
          ? academyValue.name || '' 
          : '');

      // graduationDate is already in yyyy-MM-dd (ISO) format — use directly
      const isoDate: string | undefined = graduationDate && isValidDate(graduationDate)
        ? graduationDate
        : undefined;

      // Check if we're editing a real promotion or converting a virtual one
      const isVirtual = promotionToEdit && promotionToEdit.id === 'virtual-current-belt';
      
      if (isEditing && promotionToEdit && !isVirtual) {
        // Editing a real promotion
        await updatePromotionMutation.mutateAsync({
          promotion_id: promotionToEdit.id,
          belt_color: beltColor,
          stripe_count: stripeCount,
          graduation_date: isoDate,
          instructor_name: instructorName,
          school_name: schoolName,
        });
        toast({
          title: t('profile.promotion.updated_successfully'),
          description: t('profile.promotion.updated_description'),
          duration: 2000,
        });
      } else if (isEditing && isVirtual) {
        // Converting virtual promotion to real - create new promotion
        await createPromotionMutation.mutateAsync({
          belt_color: beltColor,
          stripe_count: stripeCount,
          graduation_date: isoDate,
          instructor_name: instructorName,
          school_name: schoolName,
        });
        toast({
          title: t('profile.promotion.added_successfully'),
          description: t('profile.promotion.added_description'),
          duration: 2000,
        });
      } else {
        await createPromotionMutation.mutateAsync({
          belt_color: beltColor,
          stripe_count: stripeCount,
          graduation_date: isoDate,
          instructor_name: instructorName,
          school_name: schoolName,
        });
        toast({
          title: t('profile.promotion.added_successfully'),
          description: t('profile.promotion.added_description'),
          duration: 2000,
        });
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving promotion:', error);
      
      // Handle specific error messages
      const errorMessage = error.message;
      if (errorMessage.includes('already registered')) {
        setValidationError(t('profile.promotion.already_registered'));
      } else {
        toast({
          title: t('common.error'),
          description: t('profile.promotion.save_error', { action: (isEditing && promotionToEdit?.id !== 'virtual-current-belt') ? t('common.update') : t('common.add') }),
          variant: "destructive",
        });
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-md bg-white rounded-2xl max-h-[80vh] flex flex-col overflow-hidden p-0">
        <DialogTitle className="sr-only">{(isEditing && promotionToEdit?.id !== 'virtual-current-belt') ? t('profile.promotion.edit_title') : t('profile.promotion.add_title')}</DialogTitle>
        <DialogDescription className="sr-only">
          {(isEditing && promotionToEdit?.id !== 'virtual-current-belt') ? t('profile.promotion.edit_description') : t('profile.promotion.add_description')}
        </DialogDescription>
        <SossietyDialogHeader title={isEditing && promotionToEdit?.id !== 'virtual-current-belt' ? t('profile.promotion.edit_title') : t('profile.promotion.add_title')} titleClassName="text-lg" className="flex-shrink-0 px-6 pt-6">
          <SossietyDialogClose onClick={onClose} />
        </SossietyDialogHeader>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          <div className="space-y-4">
          {/* Belt Color */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              {t('profile.promotion.belt')} <span className="text-red-500">*</span>
            </label>
            <div className="w-full border border-black rounded-md focus-within:border-gray-700 focus-within:ring-1 focus-within:ring-gray-700">
              <Select 
              value={beltColor}
                onValueChange={(value) => setBeltColor(value as any)}
              >
                <SelectTrigger className="w-full px-3 text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent text-sm [&_svg]:text-gray-500">
                  <SelectValue placeholder={t('profile.promotion.select_belt_rank')} />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60">
                  <SelectItem value="white" className="text-white text-sm">{t('profile.promotion.white_belt')}</SelectItem>
                  <SelectItem value="blue" className="text-white text-sm">{t('profile.promotion.blue_belt')}</SelectItem>
                  <SelectItem value="purple" className="text-white text-sm">{t('profile.promotion.purple_belt')}</SelectItem>
                  <SelectItem value="brown" className="text-white text-sm">{t('profile.promotion.brown_belt')}</SelectItem>
                  <SelectItem value="black" className="text-white text-sm">{t('profile.promotion.black_belt')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stripe Count */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              {t('profile.promotion.stripe')}
            </label>
            <div className="w-full border border-black rounded-md focus-within:border-gray-700 focus-within:ring-1 focus-within:ring-gray-700">
              <Select 
                value={stripeCount.toString()} 
                onValueChange={(value) => setStripeCount(Number(value))}
              >
                <SelectTrigger className="w-full px-3 text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent text-sm [&_svg]:text-gray-500">
                  <SelectValue placeholder={t('profile.promotion.select_stripe_count')} />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60">
                  <SelectItem value="0" className="text-white text-sm">{t('profile.promotion.no_stripe')}</SelectItem>
                  <SelectItem value="1" className="text-white text-sm">{t('profile.promotion.1st_stripe')}</SelectItem>
                  <SelectItem value="2" className="text-white text-sm">{t('profile.promotion.2nd_stripe')}</SelectItem>
                  <SelectItem value="3" className="text-white text-sm">{t('profile.promotion.3rd_stripe')}</SelectItem>
                  <SelectItem value="4" className="text-white text-sm">{t('profile.promotion.4th_stripe')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Graduation Date */}
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              {t('profile.promotion.graduation_date')} <span className="text-red-500">*</span>
            </label>
            <div className="w-full border border-black rounded-md focus-within:border-gray-700 focus-within:ring-1 focus-within:ring-gray-700">
              <DateInput
                id="graduation-date"
                value={graduationDate}
                onChange={setGraduationDate}
                className="border-0"
              />
            </div>
          </div>

          {/* Instructor */}
          {shouldShowAdditionalFields && (
            <div>
              <label className="block text-sm font-medium mb-2 text-black">
                {t('profile.instructor')}
              </label>
              {isLoadingProfile && !isEditing ? (
                <div className="w-full h-12 bg-gray-200 animate-pulse rounded-md border border-gray-300 relative">
                  <div className="absolute left-12 top-1/2 transform -translate-y-1/2 w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ) : (
                <InstructorAutocomplete
                  value={instructorValue}
                  onChange={setInstructorValue}
                  placeholder={t('calendar.search_instructor_placeholder')}
                  className="text-black border-black bg-white"
                  containerClassName="w-full"
                  theme="light"
                  noPortal
                />
              )}
            </div>
          )}

          {/* Jiu Jitsu School */}
          {shouldShowAdditionalFields && (
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                {t('profile.add_your_jiu_jitsu_school')}
              </label>
              {isLoadingProfile && !isEditing ? (
                <div className="w-full h-12 bg-gray-200 animate-pulse rounded-md border border-gray-300 relative">
                  <div className="absolute left-12 top-1/2 transform -translate-y-1/2 w-40 h-3 bg-gray-300 rounded animate-pulse"></div>
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-gray-300 rounded animate-pulse"></div>
                </div>
              ) : (
                <AcademyAutocomplete
                  value={academyValue}
                  onChange={handleAcademyChange}
                  placeholder={t('profile.add_your_jiu_jitsu_school')}
                  containerClassName="w-full"
                  theme="light"
                  theme="light"
                />
              )}
            </div>
          )}

          {/* Validation Error */}
          {validationError && (
            <div className="text-red-500 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              {validationError}
            </div>
          )}
          </div>
        </form>

        {/* Buttons - Fixed at bottom */}
        <DialogFooter className="px-6 pb-6 pt-4 flex-shrink-0 border-t border-gray-200 bg-white !flex-row !justify-stretch">
          <div className="flex gap-3 w-full">
            <Button
              type="button"
              variant="outline-light"
              size="lg"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="lg"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e as any);
              }}
              disabled={isLoading || !beltColor || beltColor === undefined || !graduationDate || !isValidDate(graduationDate) || !!validationError}
              className="flex-1"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  {(isEditing && promotionToEdit?.id !== 'virtual-current-belt') ? t('profile.promotion.updating') : t('profile.promotion.adding')}
                </span>
              ) : (
                (isEditing && promotionToEdit?.id !== 'virtual-current-belt') ? t('common.update') : t('common.save')
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 